import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  Calendar, ClipboardList, Scissors, Users, Image as ImageIcon, 
  FileText, Clock, User, Palette, UserCog, CalendarOff,
  ChevronLeft, ChevronRight, ListOrdered, Home, Trophy, BarChart3, Mail, Settings, ClipboardEdit
} from 'lucide-react';

import AdminBookingsSection from './sections/AdminBookingsSection';
import AdminServicesSection from './sections/AdminServicesSection';
import AdminClientsSection from './sections/AdminClientsSection';
import AdminGallerySection from './sections/AdminGallerySection';
import AdminSettingsSection from './sections/AdminSettingsSection';
import AdminAgendaSection from './sections/AdminAgendaSection';
import AdminTextsSection from './sections/AdminTextsSection';
import AdminProfileSection from './sections/AdminProfileSection';
import AdminDesignSection from './sections/AdminDesignSection';
import AdminAbsencesSection from './sections/AdminAbsencesSection';
import AdminOperatorsSection from './sections/AdminOperatorsSection';
import AdminWaitlistSection from './sections/AdminWaitlistSection';
import AdminHomeSection from './sections/AdminHomeSection';
import AdminLoyaltySection from './sections/AdminLoyaltySection';
import AdminStatsSection from './sections/AdminStatsSection';
import AdminEmailLogSection from './sections/AdminEmailLogSection';
import AdminNotificationSettingsSection from './sections/AdminNotificationSettingsSection';
import AdminBookingFormSettings from './sections/AdminBookingFormSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardMenu = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [counts, setCounts] = useState({ bookings: 0, services: 0, clients: 0, photos: 0, waitlist: 0 });
  const tabsRef = useRef(null);

  useEffect(() => {
    fetchCounts();
  }, [activeTab]);

  const fetchCounts = async () => {
    try {
      const [bookingsRes, servicesRes, clientsRes, galleryRes, waitlistRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/services`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/waitlist`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setCounts({
        bookings: bookingsRes.data.length,
        services: servicesRes.data.length,
        clients: clientsRes.data.length,
        photos: galleryRes.data.length,
        waitlist: waitlistRes.data.length,
      });
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const tabs = [
    { id: 'stats', label: 'Statistiche', icon: BarChart3 },
    { id: 'agenda', label: 'Agenda', icon: Calendar },
    { id: 'prenotazioni', label: 'Prenotazioni', count: counts.bookings, icon: ClipboardList },
    { id: 'servizi', label: 'Servizi', count: counts.services, icon: Scissors },
    { id: 'home', label: 'Modifica Home', icon: Home },
    { id: 'foto', label: 'Foto Salone', icon: ImageIcon },
    { id: 'testi', label: 'Testi Sito', icon: FileText },
    { id: 'orari', label: 'Orari & Contatti', icon: Clock },
    { id: 'clienti', label: 'Clienti', count: counts.clients, icon: Users },
    { id: 'fedelta', label: 'Carta Fedeltà', icon: Trophy },
    { id: 'profilo', label: 'Il Mio Profilo', icon: User },
    { id: 'design', label: 'Design & Colori', icon: Palette },
    { id: 'assenze', label: 'Assenze Operatori', icon: CalendarOff },
    { id: 'operatori', label: 'Gestione Operatori', icon: UserCog },
    { id: 'waitlist', label: "Lista d'Attesa", count: counts.waitlist, icon: ListOrdered },
    { id: 'email-log', label: 'Storico Notifiche', icon: Mail },
    { id: 'notif-settings', label: 'Impostazioni Notifiche', icon: Settings },
    { id: 'booking-form', label: 'Modulo Prenotazione', icon: ClipboardEdit },
  ];

  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 200;
      tabsRef.current.scrollBy({ 
        left: direction === 'left' ? -scrollAmount : scrollAmount, 
        behavior: 'smooth' 
      });
    }
  };

  const renderActiveSection = () => {
    switch (activeTab) {
      case 'stats': return <AdminStatsSection />;
      case 'agenda': return <AdminAgendaSection />;
      case 'prenotazioni': return <AdminBookingsSection />;
      case 'servizi': return <AdminServicesSection />;
      case 'foto': return <AdminGallerySection />;
      case 'home': return <AdminHomeSection />;
      case 'testi': return <AdminTextsSection />;
      case 'orari': return <AdminSettingsSection />;
      case 'clienti': return <AdminClientsSection />;
      case 'fedelta': return <AdminLoyaltySection />;
      case 'profilo': return <AdminProfileSection />;
      case 'design': return <AdminDesignSection />;
      case 'assenze': return <AdminAbsencesSection />;
      case 'operatori': return <AdminOperatorsSection />;
      case 'waitlist': return <AdminWaitlistSection />;
      case 'email-log': return <AdminEmailLogSection />;
      case 'notif-settings': return <AdminNotificationSettingsSection />;
      case 'booking-form': return <AdminBookingFormSettings />;
      default: return <AdminAgendaSection />;
    }
  };

  return (
    <div className="min-h-screen py-6 sm:py-10" data-testid="admin-dashboard">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="admin-dashboard-title">
            Dashboard Admin
          </h1>

          {/* Scrollable Tabs */}
          <div className="relative mb-6" data-testid="admin-tabs-container">
            {/* Left scroll button */}
            <button
              onClick={() => scrollTabs('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-secondary transition-colors hidden sm:flex"
              data-testid="tabs-scroll-left"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div
              ref={tabsRef}
              className="flex gap-3 overflow-x-auto scrollbar-hide px-0 sm:px-10 pb-2"
              data-testid="admin-tabs-scroll"
            >
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`admin-tab-${tab.id}`}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 border ${
                      isActive
                        ? 'bg-foreground text-background border-foreground shadow-lg'
                        : 'bg-background text-foreground border-border hover:border-foreground/30 hover:bg-secondary'
                    }`}
                  >
                    {isActive && <Icon className="h-4 w-4" />}
                    {tab.label}
                    {tab.count !== undefined && (
                      <span className={`text-xs ${isActive ? 'opacity-80' : 'opacity-60'}`}>
                        ({tab.count})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right scroll button */}
            <button
              onClick={() => scrollTabs('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-secondary transition-colors hidden sm:flex"
              data-testid="tabs-scroll-right"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Active Section Content */}
          <div 
            className="bg-card border border-border rounded-xl p-4 sm:p-6"
            data-testid="admin-section-content"
          >
            {renderActiveSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardMenu;
